/**
 * lib/readback.ts — THE READBACK LAW (2026-09-26, the coordinator's QA of the realism program).
 *
 * three r185's `readRenderTargetPixelsAsync` on WebGPU (`WebGPUTextureUtils.copyTextureToBuffer`) pads every
 * row to 256 bytes and RETURNS THE PADDED BUFFER, sized `(h − 1) · stride + w · bytesPerTexel`. Whenever
 * `w × bytesPerTexel` is not a multiple of 256 (a 16-px RGBA16F meter region, a 2700-px RGBA8 frame — the high
 * tier's TAAU buffer) a reader that indexes `(y · w + x) · 4` walks into the padding: the auto-exposure meter
 * read five stops low that way, and a frame lever's rows drift by the padding each line. WebGL2 never pads.
 *
 * `tightenReadback` returns a TIGHT `w · h · channels` array of the same kind (the input itself when it is
 * already tight). Every readback consumer in the client goes through it; a new one must too.
 */

type Typed = ArrayLike<number> & { constructor: { name: string } };

/** The elements per padded row for a readback of `w` texels × `channels`, given the buffer's length. */
export function readbackRowStride(length: number, w: number, h: number, channels = 4): number {
  const row = w * channels;
  if (h <= 1 || length <= row * h) return row;
  return Math.floor((length - row) / (h - 1));
}

/** Strip three's 256-byte row padding from a WebGPU readback; a no-op on a tight (or WebGL2) buffer. */
export function tightenReadback<T extends Typed>(px: T, w: number, h: number, channels = 4): T {
  const row = w * channels;
  const want = row * h;
  if (px.length <= want) return px;
  const stride = readbackRowStride(px.length, w, h, channels);
  if (stride === row) return px;
  const Ctor = px.constructor as unknown as new (n: number) => T & { set(src: ArrayLike<number>, at: number): void };
  const out = new Ctor(want);
  const src = px as unknown as { subarray?: (a: number, b: number) => ArrayLike<number> };
  for (let y = 0; y < h; y++) {
    const from = y * stride;
    if (src.subarray) out.set(src.subarray(from, from + row), y * row);
    else for (let i = 0; i < row; i++) (out as unknown as number[])[y * row + i] = px[from + i];
  }
  return out as unknown as T;
}
